import { KeyboardEvent, memo, useCallback, useState } from 'react'

import { BeforeFormChangeHandler, Form, useFormData } from 'brief-form'
import { Validators } from 'ui/components'
import { LoginButton } from 'ui/components/Buttons'
import { Icon, IconName, IconSize } from 'ui/components/Icon'
import { EmailInput, InputIconPosition, TextInput, USPhoneInput } from 'ui/components/InputV2'
import { FormRow } from 'ui/themes/globalStyles'
import { AuthForm } from '~auth/components'

import { DeviceTypeSelector } from './components'

import { s } from './styles'

interface Props {
  onSubmit: (value: CreateDeviceFormShape) => Promise<any>
  logoElement: React.ReactElement
}

export type CreateDeviceFormShape = {
  type: string
  name: string
}

const placeholders = {
  sms: 'Phone number',
  email: 'Email address',
  totp: ''
}

export const CreateDeviceForm = memo(({ onSubmit, logoElement }: Props) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const onBeforeChange: BeforeFormChangeHandler<CreateDeviceFormShape> = useCallback(
    ({ value, errors, oldValue }) => {
      setError('')

      return {
        value: {
          type: value.type,
          name: value.type === 'totp' ? 'totp' : oldValue.type === value.type ? value.name : ''
        },
        errors: {
          type: errors.type,
          name: oldValue.type === value.type ? errors.name : undefined
        }
      }
    },
    [setError]
  )

  const { config, Field } = useFormData<CreateDeviceFormShape>({
    onBeforeChange,
    initialValue: {
      type: 'sms',
      name: ''
    }
  })

  const { value } = config

  const submitWrapper = useCallback(() => {
    setLoading(true)
    onSubmit(value)
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [value, onSubmit, setError, setLoading])

  return (
    <AuthForm error={error} logo={logoElement}>
      <div
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key == 'Enter') {
            event.preventDefault()
            submitWrapper()
          }
        }}
      >
        <s.Hint>
          Please, choose a type of your first MFA device. We will send you security code there. Later you can change
          preferred method in your account settings.
        </s.Hint>
        <Form config={config}>
          <FormRow>
            <s.LoginCol>
              <Field name="type" input={DeviceTypeSelector} inputProps={{}} />
            </s.LoginCol>
          </FormRow>
          {value.type !== 'totp' && (
            <FormRow>
              <Field
                required
                name="name"
                input={value.type === 'email' ? EmailInput : value.type === 'sms' ? USPhoneInput : TextInput}
                validator={Validators.required.string}
                inputProps={{
                  placeholder: placeholders[value.type],
                  style: s.inputStyle,
                  iconPosition: InputIconPosition.LEFT_CLOSE,
                  renderIcon: (
                    <Icon name={value.type === 'email' ? IconName.ENVELOPE : IconName.PHONE} size={IconSize.M} />
                  )
                }}
              />
            </FormRow>
          )}
        </Form>
        <s.Footer>
          <LoginButton loading={loading} onClick={submitWrapper}>
            SEND CODE
          </LoginButton>
        </s.Footer>
      </div>
    </AuthForm>
  )
})
